import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, CircleDollarSign, Download, Hourglass, Loader2, Search, Settings as SettingsIcon, Trash2, Wallet } from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import { Chips, Pill } from "@/components/Music";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Money, money } from "@/components/Royalty";
import { StatusPill, WriterTerms } from "@/components/Payout";
import { DatePicker } from "@/components/Calendar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, downloadFile, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cacheKey, cacheSet } from "@/lib/cache";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const CHIPS = [
  ["all", "Everyone"],
  ["ready", "Ready to pay"],
  ["below", "Below minimum"],
  ["hold", "On hold"],
];

function KV({ k, children, bold, muted }) {
  return (
    <div className={cn("flex justify-between gap-3 py-2 border-t border-line first:border-t-0 text-[13px]", muted && "text-muted2")}>
      <span className={cn(bold && "font-semibold")}>{k}</span>
      <span className={cn(bold && "font-semibold")}>{children}</span>
    </div>
  );
}

function statement(id) {
  return downloadFile(`/payouts/${id}/statement.csv`, "statement.csv").catch((e) => toast.error(apiError(e)));
}

// ------------------------------------------------------------------ one writer (publisher's side)
function WriterSheet({ w, settings, open, onOpenChange, onChanged, canAdmin, payouts }) {
  const [form, setForm] = useState({ amount: "", date: "", method: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (w) setForm({ amount: w.payable ? w.payable.toFixed(2) : "", date: new Date().toISOString().slice(0, 10), method: "", note: "" });
  }, [w?.writer_id, w?.payable]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!w) return null;
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payouts", { writer_id: w.writer_id, amount: Number(form.amount), date: form.date, method: form.method, note: form.note });
      toast.success(`Recorded ${money(Number(form.amount), { places: 2 })} paid to ${w.name}`);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const mine = payouts.filter((p) => p.writer_id === w.writer_id);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[640px] p-0 bg-card flex flex-col gap-0">
        <div className="p-6 pb-5 border-b border-line pr-12 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <SheetTitle className="font-display text-[22px] font-semibold leading-tight">{w.name}</SheetTitle>
            <SheetDescription className="text-[12.5px] text-muted2 font-mono">{w.ipi ? `IPI ${w.ipi}` : "No IPI"}</SheetDescription>
          </div>
          <StatusPill status={w.status} />
        </div>
        <div className="flex-1 overflow-y-auto">
          <section className="px-6 py-4 border-b border-line">
            <div className="eyebrow mb-1">The sum</div>
            <KV k="Their share of song income">
              <Money value={w.gross} />
            </KV>
            <KV k={`Publisher keeps ${w.rate}%${w.custom_rate ? " (their own rate)" : ""}`}>
              <Money value={-w.fee} />
            </KV>
            <KV k="Writer earns">
              <Money value={w.net} />
            </KV>
            <KV k="Already paid">
              <Money value={-w.paid} places={2} />
            </KV>
            <KV k="Balance owed" bold>
              <Money value={w.balance} />
            </KV>
            <KV k={`Payout minimum${w.custom_min ? " (their own)" : ""}`} muted>
              <span className="font-mono">{money(w.min, { places: 2 })}</span>
            </KV>
            {w.pending > 0 && (
              <p className="text-[12.5px] text-muted2 mt-2">
                Plus <Money value={w.pending} /> reported but not paid to you yet. It joins their balance once it's paid.
              </p>
            )}
            {w.held > 0 && (
              <p className="text-[12.5px] text-muted2 mt-2">
                <Pill tone="warn">Held</Pill> <Money value={w.held} /> from disputed recordings isn't in their balance. Release it on the work in Royalties if it turns out to be theirs.
              </p>
            )}
          </section>

          <section className="px-6 py-4 border-b border-line">
            <div className="eyebrow mb-2">Record a payout</div>
            {w.payable >= 0.01 ? (
              <form onSubmit={pay} className="space-y-2.5">
                {w.status !== "ready" && (
                  <p className="text-[12.5px] text-gold">{w.status === "hold" ? "This writer is on hold." : `They're ${money(w.min - w.balance, { places: 2 })} short of the minimum.`} You can still pay them if you choose.</p>
                )}
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block">
                    <span className="block text-[12px] font-semibold mb-1.5">Amount ($)</span>
                    <input className={cn(inputCls, "font-mono")} type="number" step="0.01" min="0.01" max={w.payable} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                  </label>
                  <div>
                    <span className="block text-[12px] font-semibold mb-1.5">Paid on</span>
                    <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                  </div>
                </div>
                <input className={inputCls} placeholder="How it was paid — bank transfer, PayPal, cash… (optional)" aria-label="How it was paid" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} />
                <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Mark ${money(Number(form.amount) || 0, { places: 2 })} as paid`}
                </Button>
                <p className="text-[12px] text-muted2">MetaTrack records the payment; it doesn't send money. Pay them the way you normally do, then mark it here.</p>
              </form>
            ) : (
              <p className="text-[13px] text-muted2">{w.balance < 0 ? "Their balance is below zero, so there's nothing to pay." : "There isn't a whole cent to pay yet."}</p>
            )}
          </section>

          <section className="py-4 border-b border-line">
            <div className="eyebrow px-6 mb-2">Song by song</div>
            <ul className="sm:hidden divide-y divide-line border-y border-line">
              {w.lines.map((l) => (
                <li key={`${l.source}-${l.track_id}-${l.accrued}`} className="px-5 py-2.5 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <Link to={`/catalog/${l.track_id}`} className="block text-[13px] font-semibold truncate hover:text-brand">
                      {l.title}
                    </Link>
                    <div className="text-[11.5px] text-muted2">
                      {l.source} · {l.split}% of <Money value={l.song_amount} />
                    </div>
                  </div>
                  <Money value={l.share} className="text-[12.5px] shrink-0" />
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto max-sm:hidden">
              <table className="w-full min-w-[480px]">
                <thead>
                  <tr className="bg-surface/70 border-y border-line">
                    {["Song", "Source", "Song income", "Split", "Their share"].map((h, i) => (
                      <th key={h} className={cn("t-head px-4 py-2 font-medium", i > 1 ? "text-right" : "text-left")}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {w.lines.map((l) => (
                    <tr key={`${l.source}-${l.track_id}-${l.accrued}`} className="border-b border-line">
                      <td className="px-4 py-2 text-[13px] font-semibold">
                        <Link to={`/catalog/${l.track_id}`} className="hover:text-brand">
                          {l.title}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <Pill>{l.source}</Pill>
                      </td>
                      <td className="px-4 py-2 text-right text-[12.5px]">
                        <Money value={l.song_amount} />
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-[12.5px]">{l.split}%</td>
                      <td className="px-4 py-2 text-right text-[12.5px]">
                        <Money value={l.share} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[12px] text-muted2 px-6 mt-2">Split is their part of the writer splits on the track. Edit the track to change it.</p>
          </section>

          <section className="px-6 py-4 border-b border-line">
            <div className="eyebrow mb-1">This writer's own terms</div>
            <WriterTerms writerId={w.writer_id} terms={{ rate: w.custom_rate ? w.rate : null, min: w.custom_min ? w.min : null, hold: w.hold }} defaults={settings} canEdit={canAdmin} onSaved={onChanged} />
            {!canAdmin && <p className="text-[12px] text-muted2 mt-2">Only an admin can change a writer's terms.</p>}
          </section>

          {mine.length > 0 && (
            <section className="px-6 py-4">
              <div className="eyebrow mb-1">Paid so far</div>
              {mine.map((p) => (
                <KV key={p.payout_id} k={`${formatDate(p.date)}${p.method ? ` · ${p.method}` : ""}`}>
                  <span className="font-mono">{money(p.amount, { places: 2 })}</span>
                </KV>
              ))}
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the publisher's page
function PublisherPayouts() {
  const { user } = useAuth();
  const canAdmin = ["owner", "admin"].includes(user?.role);
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "writers";
  const [data, setData] = useRemembered("payouts");
  const [mode, setMode] = useViewMode("payouts", "list");
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const { tenantId } = useTenant();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/payouts");
      setData(d);
      // fetch the disputed list ahead, so "See disputed works" opens on it at once
      if (d.totals?.held > 0) {
        api
          .get("/royalties/works", { params: { source: "MLC", statement: "all" } })
          .then(({ data: w }) => cacheSet(cacheKey("royalties:tab:MLC:all:works", tenantId), { tab: "works", ...w }))
          .catch(() => {});
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [setData, tenantId]);
  useEffect(() => {
    load();
  }, [load]);

  const writers = useMemo(() => data?.writers || [], [data]);
  const counts = useMemo(() => ({ all: writers.length, ready: writers.filter((w) => w.status === "ready").length, below: writers.filter((w) => w.status === "below").length, hold: writers.filter((w) => w.status === "hold").length }), [writers]);
  const list = writers.filter((w) => (chip === "all" || w.status === chip) && (!q || `${w.name} ${w.ipi}`.toLowerCase().includes(q.toLowerCase())));
  const open = writers.find((w) => w.writer_id === openId) || null;
  const t = data?.totals;
  const s = data?.settings;

  const payAll = async () => {
    const ok = await confirm({
      title: `Mark ${t.ready_count} writer${t.ready_count === 1 ? "" : "s"} as paid?`,
      body: `Records ${money(t.ready, { places: 2 })} in payouts, dated today. MetaTrack doesn't send the money — pay them the way you normally do. You can undo any of these from Payout history.`,
      confirmText: "Mark as paid",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/payouts/pay-ready");
      toast.success(`${r.count} payout${r.count === 1 ? "" : "s"} recorded — ${money(r.total, { places: 2 })}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const quickPay = async (w) => {
    try {
      await api.post("/payouts", { writer_id: w.writer_id, amount: w.payable });
      toast.success(`Recorded ${money(w.payable, { places: 2 })} paid to ${w.name}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const decline = async (r) => {
    if (!(await confirm({ title: `Decline ${r.writer_name}'s request?`, body: "The request is cleared and they can ask again later. Their balance doesn't change.", confirmText: "Decline", destructive: true }))) return;
    try {
      await api.post(`/payouts/requests/${r.request_id}/decline`);
      toast.success("Request declined");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const undo = async (p) => {
    if (!(await confirm({ title: `Undo the ${money(p.amount, { places: 2 })} payout to ${p.writer_name}?`, body: "It comes off the history and goes back onto their balance.", confirmText: "Undo payout", destructive: true }))) return;
    try {
      await api.delete(`/payouts/${p.payout_id}`);
      toast.success("Payout undone");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const flow = t ? [["bg-sec-pub", t.fee, "Publisher keeps"], ["bg-muted2", t.paid_out, "Already paid out"], ["bg-stock-in", t.ready, "Ready to pay"], ["bg-gold", t.carried, "Carried forward"]] : [];
  const flowTotal = Math.max(flow.reduce((a, f) => a + Math.max(0, f[1]), 0), 1e-12);

  return (
    <Layout
      title="Payouts"
      subtitle="What each writer is owed from the royalties that came in"
      action={
        <>
          {canAdmin && (
            <Button asChild variant="outline">
              <Link to="/settings?tab=writers">
                <SettingsIcon className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Payout settings</span>
              </Link>
            </Button>
          )}
          <Button className="bg-brand hover:bg-brand-hover" disabled={busy || !t?.ready_count} onClick={payAll}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin sm:mr-2" /> : <Check className="w-4 h-4 sm:mr-2" />}
            <span className="hidden sm:inline">Mark all ready as paid{t?.ready_count ? ` · ${money(t.ready, { places: 2 })}` : ""}</span>
          </Button>
        </>
      }
    >
      {!data ? (
        <div className="min-h-[70vh]">
          <LoadingPanel rows={8} />
        </div>
      ) : !data.has_statements ? (
        <div className="panel">
          <EmptyState icon={Wallet} title="Nothing to pay out yet" body="Payouts are worked out from your royalty statements. Import a statement and each writer's share appears here." action={<Button asChild className="bg-brand hover:bg-brand-hover"><Link to="/royalties/import">Import a statement</Link></Button>} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            <StatCard label="Collected" value={money(t.collected)} sub="Paid to you, on songs counted for writers" icon={CircleDollarSign} />
            <StatCard label="Publisher keeps" value={money(t.fee)} sub={`${s.rate}% by default`} icon={Wallet} />
            <StatCard label="Ready to pay" value={money(t.ready, { places: 2 })} tone={t.ready ? "good" : "default"} sub={`${t.ready_count} writer${t.ready_count === 1 ? "" : "s"} at or over their minimum`} icon={Check} onClick={() => setChip("ready")} />
            <StatCard label="Carried forward" value={money(t.carried)} tone={t.carried ? "warn" : "default"} sub={`Below the ${money(s.min, { places: 2 })} minimum, or on hold`} icon={Hourglass} onClick={() => setChip("below")} />
          </div>
          <section className="panel p-5 mb-2">
            <div className="eyebrow mb-2.5">Where the collected money goes</div>
            <div className="flex h-3.5 rounded-full overflow-hidden bg-surface">
              {flow.map(([c, v]) => (v > 0 ? <span key={c} className={cn("block h-full", c)} style={{ width: `${(v / flowTotal) * 100}%` }} /> : null))}
            </div>
            <div className="flex gap-x-5 gap-y-1 flex-wrap text-[12.5px] text-muted2 mt-2.5">
              {flow.map(([c, v, l]) => (
                <span key={l} className="inline-flex items-center gap-1.5">
                  <i className={cn("w-2.5 h-2.5 rounded-sm", c)} /> {l} {money(v)}
                </span>
              ))}
            </div>
            {(t.unassigned !== 0 || t.pending > 0 || t.held > 0) && (
              <div className="mt-3 pt-3 border-t border-line text-[12.5px] text-muted2 space-y-1">
                {t.unassigned !== 0 && (
                  <p>
                    <b className="text-ink">{money(t.unassigned)} isn't given to any writer</b> — {data.unassigned.length} song{data.unassigned.length === 1 ? "" : "s"} not linked to a track, with no writer splits, or with no writers signed to you.{" "}
                    <button className="text-brand font-semibold" onClick={() => setParams({ tab: "unassigned" })}>
                      See them
                    </button>
                  </p>
                )}
                {t.pending > 0 && s.paid_only && <p>Not included: {money(t.pending, { places: 2 })} reported but not paid to you yet.</p>}
                {t.held > 0 && (
                  <p>
                    <b className="text-ink">Held: {money(t.held)}</b> on disputed recordings — not given to any writer until you release it.{" "}
                    <Link to="/royalties?source=MLC&statement=all&chip=disputed" className="text-brand font-semibold">
                      See disputed works →
                    </Link>
                  </p>
                )}
              </div>
            )}
          </section>

          {data.requests?.length > 0 && (
            <section className="panel p-5 mt-4 border-brand-ring">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="font-display text-[18px] font-semibold flex-1">Withdrawal requests</h2>
                <Pill tone="brand">{data.requests.length} waiting</Pill>
              </div>
              {data.requests.map((r) => (
                <div key={r.request_id} className="flex items-center gap-3 py-2.5 border-t border-line flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-[13.5px] font-semibold">{r.writer_name}</div>
                    <div className="text-[12px] text-muted2">Asked {formatDate(r.requested_at)}</div>
                  </div>
                  <b className="font-mono text-[13px]">{money(r.balance, { places: 2 })}</b>
                  <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setOpenId(r.writer_id)}>
                    Pay
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decline(r)}>
                    Decline
                  </Button>
                </div>
              ))}
              <p className="text-[12px] text-muted2 mt-2">Pay them the way you normally do, then record it. Declining clears the request; their balance stays.</p>
            </section>
          )}

          <div className="flex gap-5 border-b border-line mt-4 overflow-x-auto" role="tablist">
            {[["writers", `Writers · ${writers.length}`], ["history", `Payout history · ${data.payouts.length}`], ...(data.unassigned.length ? [["unassigned", `Not assigned · ${data.unassigned.length}`]] : [])].map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "writers" ? {} : { tab: k })} className={cn("pb-2.5 px-1 text-[13.5px] font-semibold border-b-2 -mb-px whitespace-nowrap", tab === k ? "border-brand text-brand" : "border-transparent text-muted2 hover:text-ink")}>
                {l}
              </button>
            ))}
          </div>

          <div className="min-h-[50vh]">
            {tab === "writers" && (
              <>
                <StickyFilters className="space-y-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="relative flex-1 min-w-[200px] max-w-[280px]">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Writer name or IPI…" aria-label="Search writers" className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
                    </label>
                    <div className="flex-1" />
                    <ViewToggle mode={mode} onChange={setMode} />
                  </div>
                  <Chips wrap items={CHIPS.map(([key, label]) => ({ key, label, count: counts[key] }))} value={chip} onChange={setChip} />
                </StickyFilters>
                {!list.length ? (
                  <div className="panel">
                    <EmptyState icon={Wallet} title={writers.length ? "No writer matches" : "No writer has earnings yet"} body={writers.length ? "Try another filter." : "Earnings appear once statement songs are linked to tracks that have writer splits."} />
                  </div>
                ) : mode === "grid" ? (
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {list.map((w) => (
                      <article key={w.writer_id} className="panel p-4 flex flex-col gap-3">
                        <button onClick={() => setOpenId(w.writer_id)} className="text-left">
                          <div className="flex justify-between gap-2 items-start">
                            <div className="font-display text-[16px] font-semibold leading-tight hover:text-brand">{w.name}</div>
                            <StatusPill status={w.status} />
                          </div>
                          <div className="font-display text-[24px] font-semibold mt-2">
                            <Money value={w.balance} className="font-display" />
                          </div>
                          <div className="text-[12px] text-muted2 mt-0.5">
                            Publisher {w.rate}%{w.custom_rate ? " (own rate)" : ""} · {w.songs} song{w.songs === 1 ? "" : "s"}
                          </div>
                        </button>
                        <Button size="sm" variant="outline" disabled={w.status !== "ready"} onClick={() => quickPay(w)} className="mt-auto">
                          {w.status === "ready" ? `Mark ${money(w.payable, { places: 2 })} paid` : "Mark paid"}
                        </Button>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="panel overflow-hidden">
                    {/* phones: one row per writer, no sideways scrolling */}
                    <ul className="sm:hidden divide-y divide-line">
                      {list.map((w) => (
                        <li key={w.writer_id} className="px-4 py-3 flex items-start gap-3" onClick={() => setOpenId(w.writer_id)}>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold truncate">{w.name}</div>
                            <div className="font-mono text-[11px] text-muted2 truncate">{w.ipi ? `IPI ${w.ipi}` : "No IPI"}</div>
                            <div className="text-[12px] text-muted2 mt-1">
                              Publisher {w.rate}%{w.custom_rate ? " (own)" : ""} · earned <Money value={w.net} />
                            </div>
                          </div>
                          <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                            <Money value={w.balance} bold className="text-[14px]" />
                            <div className="flex items-center gap-1 flex-wrap justify-end">
                              {data.requests?.some((r) => r.writer_id === w.writer_id) && <Pill tone="brand">Requested</Pill>}
                              <StatusPill status={w.status} />
                            </div>
                            {w.status === "ready" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  quickPay(w);
                                }}
                              >
                                Mark paid
                              </Button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="overflow-x-auto max-sm:hidden">
                      <table className="w-full min-w-[920px]">
                        <thead>
                          <tr className="bg-surface/70 border-b border-line">
                            {["Writer", "Their share", "Publisher %", "Publisher keeps", "Writer earns", "Balance owed", "Status", ""].map((h, i) => (
                              <th key={h || "x"} className={cn("t-head px-4 py-2.5 font-medium", i > 0 && i < 6 ? "text-right" : "text-left")}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((w) => (
                            <tr key={w.writer_id} className="t-row cursor-pointer" onClick={() => setOpenId(w.writer_id)}>
                              <td className="t-cell">
                                <div className="font-semibold">{w.name}</div>
                                <div className="font-mono text-[11px] text-muted2">{w.ipi ? `IPI ${w.ipi}` : "No IPI"}</div>
                              </td>
                              <td className="t-cell text-right text-[12.5px]">
                                <Money value={w.gross} />
                              </td>
                              <td className="t-cell text-right font-mono text-[12.5px] whitespace-nowrap">
                                {w.rate}%{w.custom_rate && <Pill tone="brand" className="ml-1.5">own</Pill>}
                              </td>
                              <td className="t-cell text-right text-[12.5px]">
                                <Money value={w.fee} />
                              </td>
                              <td className="t-cell text-right text-[12.5px]">
                                <Money value={w.net} />
                              </td>
                              <td className="t-cell text-right text-[12.5px]">
                                <Money value={w.balance} bold />
                              </td>
                              <td className="t-cell">
                                {data.requests?.some((r) => r.writer_id === w.writer_id) && (
                                  <Pill tone="brand" className="mr-1">
                                    Requested
                                  </Pill>
                                )}
                                <StatusPill status={w.status} />
                                {w.custom_min && <div className="text-[11px] text-muted2 mt-0.5">min {money(w.min, { places: 2 })}</div>}
                              </td>
                              <td className="t-cell text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={w.status !== "ready"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    quickPay(w);
                                  }}
                                >
                                  Mark paid
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 border-t border-line text-[12.5px] text-muted2">
                      Their share is each song's income divided between the writers on the track by their splits. Click a writer for the song-by-song working.
                    </div>
                  </div>
                )}
              </>
            )}

            {tab === "history" &&
              (data.payouts.length === 0 ? (
                <div className="panel mt-4">
                  <EmptyState icon={Wallet} title="No payouts yet" body="When you mark a writer as paid, it's listed here with its statement." />
                </div>
              ) : (
                <div className="panel overflow-hidden mt-4">
                  <ul className="sm:hidden divide-y divide-line">
                    {data.payouts.map((p) => (
                      <li key={p.payout_id} className="px-4 py-3 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">{p.writer_name}</div>
                          <div className="text-[12px] text-muted2 truncate">
                            {formatDate(p.date)}
                            {p.method ? ` · ${p.method}` : ""}
                          </div>
                        </div>
                        <b className="font-mono text-[13px] shrink-0">{money(p.amount, { places: 2 })}</b>
                        <div className="flex shrink-0">
                          {p.has_statement && (
                            <Button size="sm" variant="outline" onClick={() => statement(p.payout_id)} aria-label="Statement">
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-stock-out ml-1" onClick={() => undo(p)} aria-label="Undo payout">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="overflow-x-auto max-sm:hidden">
                    <table className="w-full min-w-[680px]">
                      <thead>
                        <tr className="bg-surface/70 border-b border-line">
                          {["Paid on", "Writer", "How", "Recorded by", "Amount", ""].map((h, i) => (
                            <th key={h || "x"} className={cn("t-head px-4 py-2.5 font-medium", i === 4 ? "text-right" : "text-left")}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.payouts.map((p) => (
                          <tr key={p.payout_id} className="t-row">
                            <td className="t-cell text-[12.5px] whitespace-nowrap">{formatDate(p.date)}</td>
                            <td className="t-cell font-semibold">{p.writer_name}</td>
                            <td className="t-cell text-[12.5px] text-muted2">{p.method || "—"}</td>
                            <td className="t-cell text-[12.5px] text-muted2">{p.created_by || "—"}</td>
                            <td className="t-cell text-right font-mono text-[13px]">{money(p.amount, { places: 2 })}</td>
                            <td className="t-cell text-right whitespace-nowrap">
                              {p.has_statement && (
                                <Button size="sm" variant="outline" onClick={() => statement(p.payout_id)}>
                                  <Download className="w-3.5 h-3.5 sm:mr-1.5" />
                                  <span className="hidden sm:inline">Statement</span>
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-stock-out ml-1" onClick={() => undo(p)} aria-label="Undo payout">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

            {tab === "unassigned" && (
              <div className="panel overflow-hidden mt-4">
                <div className="px-5 py-4 text-[13px] text-muted2">This money came in, but MetaTrack can't tell which writer it belongs to. Fix the reason and it moves into their balance.</div>
                <ul className="sm:hidden divide-y divide-line border-t border-line">
                  {data.unassigned.map((u, i) => (
                    <li key={i} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">{u.title}</div>
                          <div className="text-[12px] text-muted2 mt-0.5">{u.reason}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <Money value={u.amount} className="text-[12.5px]" />
                          <div className="mt-1">
                            <Pill>{u.source}</Pill>
                          </div>
                        </div>
                      </div>
                      <Link
                        to={u.fix === "signed" ? "/writers?review=1" : u.track_id ? `/catalog/${u.track_id}` : `/royalties?source=${u.source}&work=${encodeURIComponent(u.song_code || "")}&link=1`}
                        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand mt-1.5"
                      >
                        {u.fix === "signed" ? "Signed writers" : u.track_id ? "Add splits" : "Link it"} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="overflow-x-auto max-sm:hidden">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr className="bg-surface/70 border-y border-line">
                        {["Song", "Source", "Why", "Amount", ""].map((h, i) => (
                          <th key={h || "x"} className={cn("t-head px-4 py-2.5 font-medium", i === 3 ? "text-right" : "text-left")}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.unassigned.map((u, i) => (
                        <tr key={i} className="t-row">
                          <td className="t-cell font-semibold">{u.title}</td>
                          <td className="t-cell">
                            <Pill>{u.source}</Pill>
                          </td>
                          <td className="t-cell text-[12.5px] text-muted2">{u.reason}</td>
                          <td className="t-cell text-right text-[12.5px]">
                            <Money value={u.amount} />
                          </td>
                          <td className="t-cell text-right">
                            <Link
                              to={u.fix === "signed" ? "/writers?review=1" : u.track_id ? `/catalog/${u.track_id}` : `/royalties?source=${u.source}&work=${encodeURIComponent(u.song_code || "")}&link=1`}
                              className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand whitespace-nowrap"
                            >
                              {u.fix === "signed" ? "Signed writers" : u.track_id ? "Add splits" : "Link it"} <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <WriterSheet w={open} settings={s || { rate: 0, min: 0 }} open={!!open} onOpenChange={(v) => !v && setOpenId(null)} onChanged={load} canAdmin={canAdmin} payouts={data?.payouts || []} />
    </Layout>
  );
}

// ------------------------------------------------------------------ a writer's own page
function MyPayouts() {
  const [d, setD] = useRemembered("payouts-mine");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .get("/payouts/mine")
      .then(({ data }) => setD(data))
      .catch((e) => toast.error(apiError(e)));
  }, [setD]);
  useEffect(() => {
    load();
  }, [load]);
  if (!d)
    return (
      <Layout title="Payouts" subtitle="Loading…">
        <div className="min-h-[70vh]">
          <LoadingPanel rows={6} />
        </div>
      </Layout>
    );
  const v = d.visible;
  const pub = d.publisher || "your publisher";
  const pct = d.min > 0 ? Math.max(0, Math.min(100, (d.balance / d.min) * 100)) : d.balance > 0 ? 100 : 0;
  const next = d.next_date ? formatDate(d.next_date) : `when ${pub} runs payouts`;
  const line =
    d.status === "ready"
      ? `Ready to be paid. Next payout: ${next}.`
      : d.status === "below"
      ? `Paid once you reach ${money(d.min, { places: 2 })}. Until then it carries forward.`
      : d.status === "hold"
      ? `Your payouts are on hold. Contact ${pub}.`
      : d.balance < 0
      ? "Below zero after a service took money back. It's recovered from your next earnings."
      : "Nothing owed right now.";
  const why = d.request
    ? `Requested ${formatDate(d.request.requested_at)}. ${pub} will pay it and let you know.`
    : d.status === "ready"
    ? `Ask ${pub} to pay out your ${money(d.payable ?? d.balance, { places: 2 })}.`
    : d.status === "below"
    ? `You can withdraw once your balance reaches ${money(d.min, { places: 2 })}.`
    : d.status === "hold"
    ? `Withdrawals are paused. Contact ${pub}.`
    : "Nothing new to withdraw since your last payout.";
  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/payouts/withdraw");
      toast.success(`Request sent — ${pub} will pay it and let you know`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const cols = ["Song", ...(v.show_song ? ["Song earned"] : []), ...(v.show_split ? ["Your split"] : []), v.show_fee ? "Your share" : "You earned"];
  return (
    <Layout title="Payouts" subtitle={`What ${pub} owes you, and what's been paid`}>
      <div className="max-w-[860px] space-y-4">
        <section className="panel p-5">
          <div className="eyebrow">Your balance</div>
          <div className="flex items-baseline gap-3 flex-wrap mt-1.5">
            <div className={cn("font-display text-[36px] font-semibold leading-none", d.balance < 0 && "text-stock-out")}>{money(d.balance)}</div>
            <StatusPill status={d.status} />
          </div>
          <p className="text-[13px] text-muted2 mt-2 mb-3">{line}</p>
          <div className="h-2 rounded-full bg-surface overflow-hidden">
            <div className={cn("h-full rounded-full", d.status === "ready" ? "bg-stock-in" : "bg-gold")} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-4">
            <Button className="bg-brand hover:bg-brand-hover" disabled={!d.can_withdraw || busy} onClick={withdraw}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : d.request ? "Withdrawal requested" : d.can_withdraw ? `Withdraw ${money(d.payable, { places: 2 })}` : "Withdraw"}
            </Button>
            <span className="text-[12.5px] text-muted2 flex-1 min-w-[200px]">{why}</span>
          </div>
          {v.show_fee && (
            <div className="mt-4">
              <KV k="Your share of song income">
                <Money value={d.gross} />
              </KV>
              <KV k={`Publisher's ${d.rate}%`}>
                <Money value={-d.fee} />
              </KV>
              <KV k="You earned">
                <Money value={d.earned} />
              </KV>
              <KV k="Paid to you">
                <Money value={-d.paid} places={2} />
              </KV>
            </div>
          )}
        </section>
        <section className="panel overflow-hidden">
          <div className="px-5 pt-5 pb-3 eyebrow">Earned by song</div>
          {d.songs.length === 0 ? (
            <p className="px-5 pb-5 text-[13px] text-muted2">Nothing earned yet.</p>
          ) : (
            <>
            <ul className="sm:hidden divide-y divide-line border-t border-line">
              {d.songs.map((sng, i) => (
                <li key={i} className="px-5 py-2.5 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-semibold truncate">{sng.title}</div>
                    {(v.show_song || v.show_split) && (
                      <div className="text-[11.5px] text-muted2">
                        {v.show_split ? `Your split ${sng.split}%` : ""}
                        {v.show_song && v.show_split ? " of " : ""}
                        {v.show_song && (
                          <>
                            {v.show_split ? "" : "Song earned "}
                            <Money value={sng.song_amount} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <Money value={sng.amount} className="text-[12.5px] shrink-0" />
                </li>
              ))}
            </ul>
            <div className="overflow-x-auto max-sm:hidden">
              <table className="w-full" style={{ minWidth: 260 + cols.length * 90 }}>
                <thead>
                  <tr className="bg-surface/70 border-y border-line">
                    {cols.map((h, i) => (
                      <th key={h} className={cn("t-head px-4 py-2.5 font-medium", i ? "text-right" : "text-left")}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.songs.map((sng, i) => (
                    <tr key={i} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-2.5 text-[13.5px] font-semibold">{sng.title}</td>
                      {v.show_song && (
                        <td className="px-4 py-2.5 text-right text-[12.5px]">
                          <Money value={sng.song_amount} />
                        </td>
                      )}
                      {v.show_split && <td className="px-4 py-2.5 text-right font-mono text-[12.5px]">{sng.split}%</td>}
                      <td className="px-4 py-2.5 text-right text-[12.5px]">
                        <Money value={sng.amount} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          {v.show_pending && d.pending > 0 && (
            <div className="px-5 py-3 border-t border-line text-[12.5px] text-muted2">
              <Pill tone="warn">Pending</Pill> {money(d.pending)} more is on its way. It's added once it's paid to {pub}.
            </div>
          )}
        </section>
        <section className="panel p-5">
          <div className="eyebrow mb-1">Paid to you</div>
          {d.payouts.length === 0 ? (
            <p className="text-[13px] text-muted2">No payouts yet.</p>
          ) : (
            d.payouts.map((p) => (
              <div key={p.payout_id} className="flex items-center justify-between gap-3 py-2 border-t border-line first:border-t-0">
                <div className="text-[13px]">{formatDate(p.date)}</div>
                <div className="flex items-center gap-2">
                  <b className="font-mono text-[13px]">{money(p.amount, { places: 2 })}</b>
                  {p.has_statement && (
                    <Button size="sm" variant="outline" onClick={() => statement(p.payout_id)}>
                      Statement
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </Layout>
  );
}

export default function Payouts() {
  const { user } = useAuth();
  return user?.role === "writer" ? <MyPayouts /> : <PublisherPayouts />;
}
